import { FC, PropsWithChildren, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import config from '@/lib/config';
import { AccountModel, CookieyesEventModel } from '@/lib/models';
import Cookies from 'js-cookie';

interface AnalyticsContextConfig {
	handleAnalyticOutbound(link: string): void;
	handleClickEvent(url: string): void;
	trackEvent(eventName: string, params?: Record<string, string>): void;
}

export const AnalyticsContext = createContext<AnalyticsContextConfig | undefined>(undefined);

interface AnalyticsProviderProps {
	account?: AccountModel;
	appIsInitialized: boolean;
}

export const AnalyticsProvider: FC<PropsWithChildren<AnalyticsProviderProps>> = ({
	account,
	appIsInitialized,
	children,
}) => {
	const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
	const gaIsInitialized = useRef(false);
	const { pathname, search } = useLocation();

	useEffect(() => {
		if (!analyticsAllowed || !appIsInitialized || gaIsInitialized.current) {
			return;
		}

		ReactGA.initialize(config.HACKETT_ADVISORY_GOOGLE_ANALYTICS_ID, {
			testMode: __DEV__,
			...(account && {
				gaOptions: account.analytics
			}),
			gtagOptions: {
				send_page_view: false,
			},
		});

		if (account) {
			ReactGA.set({
				userId: account.accountId,
			});
		}

		gaIsInitialized.current = true;

		// Send initial page view, delayed to let Helmet set document.title
		const timeoutId = setTimeout(() => {
			ReactGA.send({ hitType: 'pageview', page: pathname + search, title: document.title });
		}, 0);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsAllowed, appIsInitialized, account]);

	useEffect(() => {
		const consentCookie = Cookies.get('cookieyes-consent');
		setAnalyticsAllowed(!!consentCookie?.includes('consent:yes') && !!consentCookie?.includes('analytics:yes'));
	}, []);

	const handleCookieYesConsentUpdate = useCallback(({ detail }: CookieyesEventModel) => {
		setAnalyticsAllowed(!!detail?.accepted.includes('analytics'));
	}, []);

	useEffect(() => {
		// CookieYes documentation on banner interaction events
		// https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
		document.addEventListener('cookieyes_consent_update', handleCookieYesConsentUpdate);

		return () => {
			document.removeEventListener('cookieyes_consent_update', handleCookieYesConsentUpdate);
		};
	}, [handleCookieYesConsentUpdate]);

	const handleAnalyticOutbound = (link: string) => {
		const a = document.createElement('a');
		a.href = link;
		a.target = '_blank';
		a.rel = 'noopener';
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	const handleClickEvent = (url: string) => {
		ReactGA.gtag('event', 'click', {
			event_category: 'outbound',
			link_url: url,
			transport_type: 'beacon',
			outbound: 'true',
		});
	};

	const trackEvent = (eventName: string, params?: Record<string, string>) => {
		ReactGA.gtag('event', eventName, params);
	};

	return (
		<AnalyticsContext.Provider
			value={{
				handleAnalyticOutbound,
				handleClickEvent,
				trackEvent,
			}}
		>
			{children}
		</AnalyticsContext.Provider>
	);
};
